import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ModuleType } from "entities/modules/types";
import React, { useState, useEffect } from "react";
import { DropResult } from "react-beautiful-dnd";
import { COLOR_GREY_9 } from "shared/theme/colors";
import NoTableItemsMessage from "../Table/NoTableItemsMessage";
import { StyledHeader, StyledPaper } from "../Table/styled";
import DraggableRow from "./components/DraggableRow";
import DroppableTable from "./components/DroppableTable";
import { ENTERING_MODULE_ANIMATION_DURATION } from "./constants";
import { reorder } from "./helpers";
import { StyledEmptyWrapper, StyledTable } from "./styled";

type TableProps<T> = {
  title?: string;
  items: T[];
  isItemsFetched: boolean;
  isItemsLoading: boolean;
  noItemsMessage?: React.ReactNode;
  actions?: React.ReactNode;
  emptyListMessage?: React.ReactNode;
  onItemsReorder: (items: Array<T>) => void;
  reorderDisabled?: boolean;
  onRowClick?: (item: T) => void;
  getRowOpacity?: (item: T) => number | undefined;
  onRowMouseEnter?: (item: T) => void;
  onRowMouseLeave?: () => void;
  handleDeleteModuleClick?: (item: T) => void;
  newlyGeneratedModuleId?: string;
};

export default function DragNDropTileTable<
  T extends { id: string; type?: ModuleType },
>({
  title,
  items,
  isItemsLoading,
  isItemsFetched,
  noItemsMessage,
  emptyListMessage,
  onItemsReorder,
  actions,
  reorderDisabled,
  onRowClick,
  getRowOpacity,
  onRowMouseEnter,
  onRowMouseLeave,
  handleDeleteModuleClick,
  newlyGeneratedModuleId,
}: TableProps<T>) {
  const [isEntering, setIsEntering] = useState(true);

  useEffect(() => {
    if (newlyGeneratedModuleId) {
      setIsEntering(true);
      const timer = setTimeout(() => {
        setIsEntering(false);
      }, ENTERING_MODULE_ANIMATION_DURATION);
      return () => clearTimeout(timer);
    }
  }, [newlyGeneratedModuleId]);

  const onDragEnd = async (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const updated = reorder(
      items,
      result.source.index,
      result.destination.index,
    ) as Array<T>;

    onItemsReorder(updated);
  };

  const isLoading = isItemsLoading && !isItemsFetched;

  if (!isItemsLoading && !items.length) {
    return (
      <StyledPaper>
        <StyledEmptyWrapper>
          <Typography variant="bodyMMedium" color={COLOR_GREY_9}>
            {emptyListMessage}
          </Typography>
        </StyledEmptyWrapper>
      </StyledPaper>
    );
  }

  if (!isLoading && !items?.length) {
    return (
      <StyledPaper>
        {title && (
          <StyledHeader>
            <Typography>{title}</Typography>
            <Box>{actions}</Box>
          </StyledHeader>
        )}
        <Box>
          <NoTableItemsMessage message={noItemsMessage ?? ""} />
        </Box>
      </StyledPaper>
    );
  }

  return (
    <StyledTable>
      {DroppableTable(onDragEnd)({
        children: items.map((item, index, arr) => {
          const opacity = getRowOpacity ? getRowOpacity(item) : undefined;
          const itemWithOpacity = {
            ...item,
            opacity,
          };

          return (
            <DraggableRow
              isLoading={isLoading}
              key={item.id}
              draggableHidden={reorderDisabled}
              disabled={reorderDisabled || isItemsLoading || arr.length < 2}
              index={index}
              isLast={index === arr.length - 1}
              item={itemWithOpacity}
              onRowClick={onRowClick}
              onMouseEnter={() => onRowMouseEnter?.(item)}
              onMouseLeave={() => onRowMouseLeave?.()}
              handleDeleteModuleClick={handleDeleteModuleClick}
              newlyGeneratedModuleId={newlyGeneratedModuleId}
              isEntering={isEntering}
            />
          );
        }),
      })}
    </StyledTable>
  );
}

DragNDropTileTable.defaultProps = {
  title: "",
  actions: null,
  noItemsMessage: null,
};
